import { useState, useEffect, useRef } from "react";
import {
  collection, addDoc, onSnapshot, query, orderBy, doc, updateDoc, setDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { clienteDb } from "../utils/clienteAuth";

/**
 * Chat em tempo real de um pedido.
 * Usado pelo CLIENTE (loja pública) e pelo VENDEDOR (app).
 *
 * props:
 *  - vendedorId: uid do vendedor (dono da loja)
 *  - pedidoId: id do pedido
 *  - autor: "cliente" | "vendedor"
 *  - altura: altura da área de mensagens (default 320)
 *  - colecao: onde a conversa mora em users/{vendedorId}/ — "pedidos_online"
 *    (chat de um pedido, default) ou "conversas_loja" (conversa livre com a
 *    loja, pedidoId = uid do cliente)
 *  - clienteInfo: { uid, nome } do cliente — só na conversa livre, pra criar
 *    o doc da conversa e identificar quem escreveu na notificação
 */
export function ChatPedido({ vendedorId, pedidoId, autor, altura = 320, colecao = "pedidos_online", clienteInfo }) {
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const fimRef = useRef(null);

  const basePath = `users/${vendedorId}/${colecao}/${pedidoId}`;
  // Cliente opera no banco autenticado; vendedor no banco do app admin
  const fdb = autor === "cliente" ? clienteDb : db;

  // Escutar mensagens em tempo real
  useEffect(() => {
    if (!vendedorId || !pedidoId) return;
    const q = query(
      collection(fdb, `${basePath}/mensagens`),
      orderBy("ts", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMensagens(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (e) => console.error("Erro no chat:", e));
    return () => unsub();
  }, [vendedorId, pedidoId, colecao]); // eslint-disable-line

  // Auto-scroll para a última mensagem
  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensagens]);

  const enviar = async (e) => {
    e?.preventDefault();
    const msg = texto.trim();
    if (!msg || enviando) return;
    setEnviando(true);
    setTexto("");
    try {
      await addDoc(collection(fdb, `${basePath}/mensagens`), {
        texto: msg,
        autor,
        ts: Date.now(),
        created_at: serverTimestamp(),
        // Conversa livre: a notificação pro vendedor precisa saber quem escreveu
        ...(clienteInfo?.nome ? { autorNome: clienteInfo.nome } : {})
      });
      // Atualiza resumo (para badges/ordenação)
      const resumo = {
        ultimaMsg: msg,
        ultimaMsgAutor: autor,
        updated_at: Date.now(),
        ...(autor === "cliente"
          ? { naoLidoVendedor: true }
          : { naoLidoCliente: true })
      };
      if (colecao === "pedidos_online") {
        await updateDoc(doc(fdb, basePath), resumo);
      } else {
        // Conversa livre: o doc nasce junto com a 1ª mensagem do cliente
        await setDoc(doc(fdb, basePath), { ...(clienteInfo ? { cliente: clienteInfo } : {}), ...resumo }, { merge: true });
      }
    } catch (err) {
      console.error("Erro ao enviar:", err);
      setTexto(msg); // devolve o texto em caso de falha
    } finally {
      setEnviando(false);
    }
  };

  const corEnviada = autor === "cliente" ? "#00d4aa" : "#3b82f6";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Mensagens */}
      <div style={{
        flex: 1,
        minHeight: altura,
        maxHeight: altura,
        overflowY: "auto",
        padding: "12px",
        background: "#0a1628",
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        gap: 8
      }}>
        {mensagens.length === 0 ? (
          <div style={{ margin: "auto", color: "#64748b", fontSize: 13, textAlign: "center" }}>
            Nenhuma mensagem ainda.<br />Inicie a conversa! 👋
          </div>
        ) : (
          mensagens.map(m => {
            const minha = m.autor === autor;
            return (
              <div
                key={m.id}
                style={{
                  alignSelf: minha ? "flex-end" : "flex-start",
                  maxWidth: "78%",
                  background: minha ? corEnviada : "#1e3a5f",
                  color: minha ? "#0a1628" : "#f1f5f9",
                  padding: "8px 12px",
                  borderRadius: 12,
                  borderBottomRightRadius: minha ? 2 : 12,
                  borderBottomLeftRadius: minha ? 12 : 2,
                  fontSize: 13,
                  lineHeight: 1.4,
                  wordBreak: "break-word"
                }}
              >
                {m.texto}
              </div>
            );
          })
        )}
        <div ref={fimRef} />
      </div>

      {/* Input */}
      <form onSubmit={enviar} style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          maxLength={1000}
          placeholder="Digite uma mensagem..."
          style={{
            flex: 1,
            padding: "10px 14px",
            background: "#1e3a5f",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 24,
            color: "#f1f5f9",
            fontSize: 13,
            outline: "none"
          }}
        />
        <button
          type="submit"
          disabled={enviando || !texto.trim()}
          style={{
            background: texto.trim() ? corEnviada : "#334155",
            color: texto.trim() ? "#0a1628" : "#64748b",
            border: "none",
            borderRadius: "50%",
            width: 42,
            height: 42,
            cursor: texto.trim() ? "pointer" : "default",
            fontSize: 18,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          ➤
        </button>
      </form>
    </div>
  );
}
